import { useFormik } from "formik";
import type React from "react";
import { useTranslation } from "react-i18next";

import { useInviteManagement } from "hooks/state/useInviteManagement";
import { useDomainHandler } from "hooks/useDomainHandler";
import { useProduct } from "hooks/useProduct";

import { Note } from "components/Note/Note";

import { CssButton, CssFlex, CssGrid } from "styles/helpers/layout";

import {
  type AddStudentToEmailInviteBundleFormFields,
  buildAddStudentToEmailInviteBundleFormSchema,
} from "./AddStudentToEmailInviteBundleForm.schema";
import { StyledInput } from "./AddStudentToEmailInviteBundleForm.styled";

interface AddStudentToEmailInviteBundleFormProps {
  emailInviteBundleId: string;
}

export const AddStudentToEmailInviteBundleForm: React.FC<
  AddStudentToEmailInviteBundleFormProps
> = ({ emailInviteBundleId }) => {
  const { t } = useTranslation("teacher-dashboard");
  const { getStudentTermByDomain } = useDomainHandler();

  const { fetchEmailInviteBundleDetails, addInvitesToEmailInvitationBundle } =
    useInviteManagement();
  const { fetchInvitationBundleList } = useProduct();

  const formik = useFormik<AddStudentToEmailInviteBundleFormFields>({
    initialValues: {
      studentEmail: "",
    },
    validationSchema: buildAddStudentToEmailInviteBundleFormSchema(),
    validateOnChange: true,
    onSubmit: async (values) => {
      const { success: addedEmails } = await addInvitesToEmailInvitationBundle(
        emailInviteBundleId,
        [values.studentEmail],
      );

      // Force refresh of invite list and details in case of success,
      // so individual invite table and usage data are up to date
      if (addedEmails.length > 0) {
        fetchEmailInviteBundleDetails(emailInviteBundleId);
        fetchInvitationBundleList();
      }

      // Reset form fields after submit is complete
      formik.setSubmitting(false);
      formik.resetForm();
    },
  });

  if (!emailInviteBundleId) {
    return null;
  }

  return (
    <form
      data-cy="add-student-to-email-invite-bundle-form"
      noValidate
      onSubmit={formik.handleSubmit}
    >
      <CssFlex flexDirection="column" gap={1}>
        <CssGrid gridTemplateColumns="1fr auto" gap={1}>
          <StyledInput
            dataCy="additional-student-email"
            aria-label={t(
              "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.addStudentToEmailInviteBundleForm.fields.studentEmail.label",
              {
                studentTerm: getStudentTermByDomain(),
              },
            )}
            placeholder={t(
              "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.addStudentToEmailInviteBundleForm.fields.studentEmail.placeholder",
              { studentTerm: getStudentTermByDomain() },
            )}
            hasError={!!(formik.errors.studentEmail && formik.touched.studentEmail)}
            id="add-student-to-email-invite-bundle-form-email-input"
            name="studentEmail"
            type="email"
            value={formik.values.studentEmail}
            onChange={formik.handleChange}
          />
          <CssButton
            dataCy="add-additional-student-button"
            variant="primary"
            type="submit"
            disabled={!formik.isValid || formik.isSubmitting}
          >
            {t(
              "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.addStudentToEmailInviteBundleForm.submitButton",
              { studentTerm: getStudentTermByDomain() },
            )}
          </CssButton>
        </CssGrid>
        {!!formik.errors.studentEmail && <Note variant="error">{formik.errors.studentEmail}</Note>}
      </CssFlex>
    </form>
  );
};
